// src/pinball/components/PinballBoard.tsx - Canvas wrapper component

import React, { useRef, useEffect, useCallback } from 'react';
import { PinballRenderer } from '../PinballRenderer';
import { TableThemeConfig, PinballGameState, VisualEffect } from '../types';
import './PinballBoard.css';

interface PinballBoardProps {
  tableConfig: TableThemeConfig;
  state: PinballGameState;
  effects: VisualEffect[];
  onCanvasReady?: (canvas: HTMLCanvasElement) => void;
}

export const PinballBoard: React.FC<PinballBoardProps> = ({
  tableConfig,
  state,
  effects,
  onCanvasReady,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rendererRef = useRef<PinballRenderer | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleResize = useCallback(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container || !rendererRef.current) return;

    const rect = container.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    rendererRef.current.resize(rect.width, rect.height, dpr);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    rendererRef.current = new PinballRenderer(canvas, tableConfig);
    rendererRef.current.initializeLayers();
    handleResize();

    onCanvasReady?.(canvas);

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [tableConfig, handleResize, onCanvasReady]);

  useEffect(() => {
    if (!rendererRef.current) return;
    rendererRef.current.renderDynamicLayer(state, 1);
    rendererRef.current.renderEffectsLayer(effects);
  }, [state, effects]);

  return (
    <div className="pinball-board-container" ref={containerRef} data-testid="pinball-board">
      <canvas ref={canvasRef} className="pinball-canvas" />
    </div>
  );
};
